import { inject, Injectable, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { compareArrays } from '../utils/functions';

import { CalculationMapperService } from './calculation-mapper.service';
import calculatorBaseForm from './calculator-base-form';

import {
	DynamicDataRecord,
	DynamicForm,
	DynamicFormBuilderService,
} from 'projects/client/src/app/service/dynamic-form-builder.service';
import {
	CalculationRequestDto,
	CalculationResultResponseDto,
	ChannelTypeAbbreviation,
	DynamicDtoConfigDto,
	ProductType,
} from 'shared/openapi/models';
import { DynamicDtoConfigControllerService } from 'shared/openapi/services';

@Injectable({
	providedIn: 'root',
})
export class DynamicCalculationFormService {
	private dynamicDtoConfigService = inject(DynamicDtoConfigControllerService);
	private calculationMapperService = inject(CalculationMapperService);
	private dynamicFormBuilderService = inject(DynamicFormBuilderService);

	public isLoading = signal(true);
	public finishLoading!: CallableFunction;
	public configLoaded = new Promise((resolve) => {
		this.finishLoading = resolve;
	});

	public productTypes = signal<ProductType[]>([], {
		equal: compareArrays,
	});
	public form = signal<DynamicForm<typeof calculatorBaseForm.controls> | null>(null);

	async createForm(productTypes: ProductType[], calculationRequest?: CalculationRequestDto) {
		if (!productTypes.length) {
			console.info('skip config loading, no product types set');
			this.isLoading.set(false);
			return;
		}

		this.isLoading.set(true);
		this.productTypes.set(productTypes);

		try {
			const config = await lastValueFrom(
				this.dynamicDtoConfigService.getCalculationDynamicDtoConfig({
					productTypes,
					channelType: ChannelTypeAbbreviation.Web,
				})
			);

			const request: CalculationRequestDto = calculationRequest ?? {};

			this.form()?.destroy();
			this.form.set(this.dynamicFormBuilderService.build(config, request as DynamicDataRecord));

			this.finishLoading();
			if (calculationRequest) {
				this.patchFormValueFromDto(calculationRequest);
			}
		} finally {
			this.isLoading.set(false);
		}
	}

	createFormWithConfig(calculationRequest?: CalculationRequestDto, config?: DynamicDtoConfigDto) {
		if (!config || !calculationRequest || !calculationRequest.productTypes) {
			console.info('skip config loading, no product types set');
			this.isLoading.set(false);
			return;
		}

		this.isLoading.set(true);
		this.productTypes.set(calculationRequest?.productTypes);

		try {
			const request: CalculationRequestDto = calculationRequest ?? {};

			this.form()?.destroy();
			this.form.set(this.dynamicFormBuilderService.build(config, request as DynamicDataRecord));

			this.finishLoading();
			if (calculationRequest) {
				this.patchFormValueFromDto(calculationRequest);
			}
		} finally {
			this.isLoading.set(false);
		}
	}

	async createConfiguredFormFromDto(calculationRequest: CalculationRequestDto): Promise<void> {
		this.configLoaded = new Promise((resolve) => {
			this.finishLoading = resolve;
		});

		const productTypes = calculationRequest.productTypes ?? [];
		await this.createForm(productTypes, calculationRequest);
		this.patchFormValueFromDto(calculationRequest);
	}

	patchFormWithMappedResult(result: CalculationResultResponseDto) {
		const isPurchasePriceOfPlannedPropertyKnown =
			this.form()!.controls.propertyData?.controls?.isPurchasePriceOfPlannedPropertyKnown.value;
		if (isPurchasePriceOfPlannedPropertyKnown === false) {
			result.purchasePriceOfPlannedProperty = 0;
		}
		const patchedForm = this.calculationMapperService.patchFormWithCalculationResultCriteria(
			this.form()!,
			this.productTypes(),
			result
		);

		this.form.set(patchedForm as DynamicForm<typeof calculatorBaseForm.controls>);
	}

	patchFormValueFromDto(dto: CalculationRequestDto) {
		const formData = this.calculationMapperService.mapCalculationRequestDtoToWebform(dto);
		this.form()?.patchValue(formData);
	}

	mapCurrentFormToCalculationRequest(): CalculationRequestDto {
		const form = this.form();
		const productTypes = this.productTypes();
		if (!form) {
			throw new Error('Form is not initialized');
		}

		if (!productTypes) {
			throw new Error('Product types are not initialized');
		}

		return this.calculationMapperService.mapCalculationWebformToDto(form, productTypes);
	}
}
